/* eslint-disable @typescript-eslint/no-misused-promises */

import { useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useTheme } from '@mui/material/styles';
import { Theme } from '@mui/material/styles';
import useIsMobile from 'hooks/useIsMobile';
import { FoundersNoteData } from '../../types/foundersNotes';
import { foundersNotesSelectedNoteState } from '../../states/foundersNotes';
import { isAdmin, isValidTokenOverrideTimeframe, shadeColor } from '../../util';
import { screenWidthState, userDetailsState } from '../../states';
import { useNavigate, useParams } from 'react-router-dom';
import { InfoButton } from 'components';
import { FoundersNoteReview } from 'components/foundersNotes/FoundersNoteReview';
import { linkContent } from '../../components/admin/AdminContentList';
import { PrintButton } from '../../components/shared/PrintButton';
import useReviewFoundersNotes from 'hooks/foundersNotes/useReviewFoundersNotes';
import FoundersNotesViewer from 'components/foundersNotes/FoundersNotesViewer';

const FoundersNotes = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const userDetails = useRecoilValue(userDetailsState);
  const [selectedNote, setSelectedNote] =
    useRecoilState<FoundersNoteData | null>(foundersNotesSelectedNoteState);
  const screenWidth = useRecoilValue(screenWidthState);
  const [sidebarOpen, setSidebarOpen] = useState(screenWidth >= 1_000);
  const [printTheme, setPrintTheme] = useState<Theme | undefined>();
  const noteContentRef = useRef<HTMLDivElement>(null);

  const { onOpenReview } = useReviewFoundersNotes();
  const { id, previewKey } = useParams();

  const showSidebar =
    previewKey == null &&
    (userDetails != null || isValidTokenOverrideTimeframe());

  const handleNoteSelect = (note: FoundersNoteData) => {
    setSelectedNote(note);
    if (previewKey == null && note?.key) {
      navigate(
        `/foundersNotes/${encodeURIComponent(
          note.key.replace('founders-notes/', ''),
        )}`,
        { replace: true },
      );
    }
  };

  const headerBar = (
    <Box
      sx={{
        paddingRight: 5,
        paddingBottom: 5,
        borderBottom: `1px solid ${shadeColor(
          theme.palette.background.paper,
          -8,
        )}`,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      <Box
        sx={{
          justifySelf: 'flex-end',
          gap: '4px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {isAdmin(userDetails) && (
          <Button
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              height: '30px',
              whiteSpace: 'nowrap',
            }}
            onClick={async () => {
              await linkContent(
                selectedNote?.title,
                selectedNote?.freePreviewKey,
              );
            }}
          >
            (Admin Only) Free link
          </Button>
        )}
        {userDetails != null && (
          <Button
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              height: '30px',
              whiteSpace: 'nowrap',
            }}
            onClick={async () => await onOpenReview()}
          >
            Review
          </Button>
        )}
        {!isMobile && (
          <PrintButton
            content={noteContentRef.current}
            onPrintTheme={setPrintTheme}
          />
        )}
        <InfoButton key="founders-note-info" articleKey="founders-note-info" />
        {showSidebar && !sidebarOpen && !isMobile && (
          <Tooltip title="Show notes list">
            <IconButton
              onClick={() => setSidebarOpen(true)}
              color="primary"
              size="small"
            >
              <ListAltIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <FoundersNotesViewer
        onNoteSelect={handleNoteSelect}
        initialNoteId={id ?? previewKey}
        isPreview={previewKey != null}
        headerSlot={headerBar}
        showSidebar={showSidebar}
        sidebarOpen={sidebarOpen}
        onSidebarOpenChange={setSidebarOpen}
        printTheme={printTheme}
        noteContentRef={noteContentRef}
      />
      <FoundersNoteReview selectedNote={selectedNote} />
    </>
  );
};

export default FoundersNotes;
